import { Link } from "react-router-dom";
import { CheckCircle2, ShieldAlert } from "lucide-react";

import { useAuth, useAuthorization } from "@/features/auth";
import { Button } from "@/shared/components/ui/button";
import { CardContainer } from "@/shared/components/CardContainer";
import { MobileHeader } from "@/shared/components/MobileHeader";
import { ResponsiveViewWrapper } from "@/shared/components/ResponsiveViewWrapper";

/**
 * Demonstrates a protected route guarded by `requiredPermission="students.read"`.
 * Formatted as a mobile-in-browser screen inside a branded mobile device frame.
 */
export function StudentsPage() {
  const { user, activeTenant } = useAuth();
  const { grantedPermissions } = useAuthorization();

  return (
    <ResponsiveViewWrapper
      maxMobileWidth={430}
      className="bg-frame-surface"
      scrollable={false}
      aria-label="پرتال مدیریت دانش‌آموزان"
    >
      <MobileHeader
        title="کایرسافت"
        subtitle="مدیریت دانش‌آموزان"
        showBack={true}
        backTo="/"
      />

      <div
        className="flex-1 space-y-4 overflow-x-hidden overflow-y-auto p-4 select-none"
        dir="rtl"
      >
        {/* Authorization Banner */}
        <div className="flex items-center gap-2.5 rounded-2xl border border-emerald-200 bg-emerald-50 p-3.5 text-xs text-emerald-800 shadow-xs">
          <CheckCircle2
            className="h-5 w-5 shrink-0 text-emerald-600"
            aria-hidden="true"
          />
          <div>
            <p className="font-bold">دسترسی تأیید شد</p>
            <p className="text-[11px] text-emerald-700">
              مجوز <code className="font-mono font-bold">students.read</code>{" "}
              برای شما فعال است.
            </p>
          </div>
        </div>

        {/* User Context Card */}
        <CardContainer variant="soft" className="space-y-2.5 p-4">
          <h2 className="text-slate-dark text-xs font-bold">اطلاعات دسترسی</h2>
          <p className="text-slate-muted text-xs leading-5">
            کاربر:{" "}
            <strong className="text-slate-800">{user?.displayName}</strong>
            <br />
            مدرسه:{" "}
            <strong className="text-slate-800">
              {activeTenant?.tenantName}
            </strong>
          </p>
        </CardContainer>

        {/* Permissions Inventory */}
        <CardContainer variant="soft" className="space-y-3 p-4">
          <div className="flex items-center gap-2 text-xs font-bold text-slate-700">
            <ShieldAlert className="text-brand h-4 w-4" aria-hidden="true" />
            <span>مجوزهای فعال در این مدرسه:</span>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {grantedPermissions.map((perm) => (
              <span
                key={perm}
                className="bg-brand-subtle text-brand border-brand/20 rounded-lg border px-2.5 py-1 font-mono text-[11px] font-bold"
              >
                {perm}
              </span>
            ))}
          </div>
        </CardContainer>

        <div className="pt-2">
          <Button
            asChild
            variant="outlineBrand"
            size="touch"
            className="w-full text-xs font-bold"
          >
            <Link to="/">بازگشت به صفحه اصلی</Link>
          </Button>
        </div>
      </div>
    </ResponsiveViewWrapper>
  );
}
